/**
 * @author Mevan Nirosh
 * @version 0.1
 * @since 7/31/2023
 */

import React, { useState, useRef } from "react"
import { Box, Fab, TextField, Button } from "@mui/material"
import { filterGridSx, filterActionsSx } from "../../components/filterLayout"
import OrgsDropdown from "../../components/orgsDropdown/OrgsDropdown"
import { Tabs, Tab, Badge } from "react-bootstrap"
import ApprovedAPKTable from "./components/ApprovedAPKTable"
import PendingAPKTable from "./components/PendingAPKTable"
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined"
import ApkUploadModal from "./components/ApkUploadModal"
import SearchIcon from "@mui/icons-material/Search"
import ClearIcon from "@mui/icons-material/Clear"
import FilterByDropdown from "../../components/filterByDropdown/FilterByDropdown"
import GroupsDropdown from "../../components/groupsDropdown/GroupsDropdown"
import AppTypeDropdown from "../../components/appTypeDropdown/AppTypeDropdown"
import { isFilterReady } from "../../components/filterByDropdown/filterValidation"

const AppManagement = () => {
  const selectOrgRef = useRef(null)
  const selectGrpRef = useRef(null)
  const selectFilterTypeRef = useRef(null)
  const selectAppTypeRef = useRef(null)

  const AUTO_APPROVE = localStorage.getItem("SET_AUTO_APPROVE")

  const [orgCode, setOrgCode] = useState()
  const [grpId, setGrpId] = useState()
  const [appIDorType,setAppIDorType] =useState()
  const [terminalSerial, setTerminalSerial] = useState()

  const [pendingCount, setPendingCount] = useState(0)
  const [isChange, setIsChange] = useState(false)
  const [isPendingChange, setIsPendingChange] = useState(false)
  const [updateData, setUpdateData] = useState(false)

  const [uploadModalOpen, setUploadModalOpen] = useState(false)

  const [filterByType, setFilterByType] = useState()
  const [filterByVal, setFilterByVal] = useState()
  const [isOrgDisabled, setIsOrgDisabled] = useState(true)
  const [isGrpDisabled, setIsGrpDisabled] = useState(true)
  const [istrmnlDisabled, setIsTrmnlDisabled] = useState(true)
  const [isSearch, setIsSearch] = useState(false)

  const handleFilterByChange = (e) => {
    selectOrgRef.current.clearValue()
    selectGrpRef.current.clearValue()
    const value = e?.value
    setFilterByType(value)
    setOrgCode(undefined)
    setGrpId(undefined)
    setTerminalSerial("")
    setFilterByVal(undefined)
    if (value === "F_ORG_CODE") {
      setIsOrgDisabled(false)
      setIsGrpDisabled(true)
      setIsTrmnlDisabled(true)
    } else if (value === "F_GRP_ID") {
      setIsOrgDisabled(false)
      setIsGrpDisabled(false)
      setIsTrmnlDisabled(true)
    } else if (value === "F_TRMNL_SERIAL") {
      setIsOrgDisabled(true)
      setIsGrpDisabled(true)
      setIsTrmnlDisabled(false)
    } else {
      setIsOrgDisabled(true)
      setIsGrpDisabled(true)
      setIsTrmnlDisabled(true)
    }
  }
  const handleOrgChange = (event) => {
    const value = event?.value
    selectGrpRef.current?.clearValue()
    setGrpId(undefined)
    setFilterByVal(filterByType === "F_ORG_CODE" ? value : undefined)
    setOrgCode(value)
  }
  const handleGrpChange = (event) => {
    const value = event?.value
    setFilterByVal(value)
    setGrpId(value)
  }
   const handleAppTypeChange = (event) => {
     const value = event?.value
     setAppIDorType(value)
   }

  const handleTerminalSerialChange = (e) => {
    const value = e.target.value
    setFilterByVal(value)
    setTerminalSerial(value)
  }
  const clearFilters = () => {
    selectOrgRef.current.clearValue()
    selectGrpRef.current.clearValue()
    selectFilterTypeRef.current.clearValue()
    selectAppTypeRef.current.clearValue()
    setIsSearch(false)
    setTerminalSerial("")
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
        <h1>Application Management</h1>
        <Fab
          onClick={() => setUploadModalOpen(true)}
          color="info"
          variant="extended"
        >
          <CloudUploadOutlinedIcon sx={{ mr: 1 }} />
          Upload APK
        </Fab>
      </div>
      <Box sx={{ ...filterGridSx, mt: 3 }}>
        <FilterByDropdown
          onChange={handleFilterByChange}
          value={filterByType}
          selectRef={selectFilterTypeRef}
        />

        {!istrmnlDisabled && <TextField
          name="terminalSerial"
          value={terminalSerial || ""}
          type="text"
          variant="outlined"
          color="secondary"
          label="Terminal Serial"
          onChange={handleTerminalSerialChange}
          disabled={istrmnlDisabled}
        />}
        <OrgsDropdown
          orgCode={orgCode ? orgCode : ""}
          onChange={handleOrgChange}
          isOrgDisabled={isOrgDisabled}
          selectRef={selectOrgRef}
        />
        <GroupsDropdown
          key={orgCode || "no-org"}
          orgCode={orgCode ? orgCode : ""}
          groupId={grpId ? grpId : ""}
          onChange={handleGrpChange}
          isGrpDisabled={isGrpDisabled || !orgCode}
          selectRef={selectGrpRef}
        />
        <AppTypeDropdown
          filterType={filterByType}
          filterByVal={filterByVal}
          onChange={handleAppTypeChange}
          selectRef={selectAppTypeRef}
        />
      </Box>
      <Box sx={filterActionsSx}>
        <Button
          variant="contained"
          color="warning"
          startIcon={<SearchIcon />}
          disabled={!isFilterReady(filterByType, orgCode, grpId, terminalSerial)}
          onClick={() => setIsSearch(true)}
        >
          Search
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={<ClearIcon />}
          onClick={clearFilters}
        >
          Clear
        </Button>
      </Box>
      <div>
        <Tabs
          defaultActiveKey="Approved"
          id="uncontrolled-tab-example"
          className="mb-3"
        >
          <Tab eventKey="Approved" title={"Approved"}>
            <ApprovedAPKTable
              updateData={updateData}
              setUpdateData={setUpdateData}
              grpId={grpId}
              orgCode={orgCode}
              terminalSerial={terminalSerial}
              isSearch={isSearch}
              setIsSearch={setIsSearch}
              filterType={filterByType}
              filterByVal={filterByVal}
              appIDorType={appIDorType}
            />
          </Tab>
          {AUTO_APPROVE === "false" && (
            <Tab
              eventKey="Pending"
              title={
                <div>
                  Pending{" "}
                  {pendingCount !== 0 && (
                    <Badge bg="danger">{pendingCount}</Badge>
                  )}
                </div>
              }
            >
              <PendingAPKTable
                orgCode={orgCode}
                setPendingCount={setPendingCount}
                updateData={updateData}
                setUpdateData={setUpdateData}
                isSearch={isSearch}
                setIsSearch={setIsSearch}
                filterType={filterByType}
                filterByVal={filterByVal}
              />
            </Tab>
          )}
        </Tabs>
      </div>
      <ApkUploadModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        orgCode={orgCode}
        defaultTab="Upload"
        fromGroup={false}
      />
    </div>
  )
}

export default AppManagement
